// Custom hook for beacon signal simulation

import { useState, useEffect, useCallback, useRef } from 'react';
import config from '../../../../config';
import {
  TEST_BEACONS,
  TEST_SENSORS,
  DEFAULT_SIGNAL_STRENGTH,
  DEFAULT_PING_INTERVAL,
  DEFAULT_BATTERY_LEVEL,
  DEFAULT_SENSOR_BATTERY,
  SIGNAL_VARIANCE,
  SENSOR_TEMP_VARIANCE,
  SENSOR_HUMIDITY_VARIANCE,
  IBEACON_UUID,
  IBEACON_TX_POWER,
} from '../constants';
import { generateGatewayId, toHex, generateSensorRawData } from '../utils';
import type { Room, BeaconLocations, BeaconBatteries, GatewayRoomMap, SensorStates, SensorState } from '../types';

export interface UseSimulationReturn {
  // State
  isRunning: boolean;
  signalStrength: number;
  pingInterval: number;
  selectedBeacon: string | null;
  beaconLocations: BeaconLocations;
  gatewayRoomMap: GatewayRoomMap;
  lastSync: Date | null;
  syncCount: number;
  sensorStates: SensorStates;

  // Actions
  setIsRunning: (running: boolean) => void;
  setSignalStrength: (strength: number) => void;
  setPingInterval: (interval: number) => void;
  setSelectedBeacon: (beaconId: string | null) => void;
  moveBeaconToRoom: (beaconId: string, room: Room | null) => void;
  clearAllLocations: () => void;
  sendSignals: () => Promise<void>;
  updateSensorState: (sensorId: string, updates: Partial<SensorState>) => void;

  // Helpers
  getBeaconLocationDisplay: (beaconId: string) => string | null;
  getBeaconsInRoom: (room: Room) => typeof TEST_BEACONS;
  getRoomForGateway: (gatewayId: string) => Room | undefined;
  getGatewayForRoom: (room: Room) => string;
}

export const useSimulation = (organizationId: string | undefined, rooms: Room[]): UseSimulationReturn => {
  // State
  const [isRunning, setIsRunning] = useState(false);
  const [signalStrength, setSignalStrength] = useState(DEFAULT_SIGNAL_STRENGTH);
  const [pingInterval, setPingInterval] = useState(DEFAULT_PING_INTERVAL);
  const [selectedBeacon, setSelectedBeacon] = useState<string | null>(null);
  const [beaconLocations, setBeaconLocations] = useState<BeaconLocations>({});
  const [beaconBatteries] = useState<BeaconBatteries>(
    TEST_BEACONS.reduce((acc, b) => ({ ...acc, [b.id]: DEFAULT_BATTERY_LEVEL }), {})
  );
  const [gatewayRoomMap, setGatewayRoomMap] = useState<GatewayRoomMap>({});
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncCount, setSyncCount] = useState(0);

  // Use ref for V3Info interval tracking to avoid dependency loop
  // (syncCount in useCallback deps would cause infinite re-renders)
  const syncCountRef = useRef(0);

  // Sensor state - initialize from TEST_SENSORS defaults
  const [sensorStates, setSensorStates] = useState<SensorStates>(() =>
    TEST_SENSORS.reduce(
      (acc, s) => ({
        ...acc,
        [s.id]: {
          temperature: s.defaultTemperature,
          humidity: s.defaultHumidity,
          enabled: true,
        },
      }),
      {}
    )
  );

  // Update sensor state
  const updateSensorState = useCallback((sensorId: string, updates: Partial<SensorState>) => {
    setSensorStates(prev => ({
      ...prev,
      [sensorId]: { ...prev[sensorId], ...updates },
    }));
  }, []);

  // Build gateway to room mapping when rooms change
  useEffect(() => {
    const map: GatewayRoomMap = {};
    rooms.forEach(room => {
      const gwId = generateGatewayId(room);
      map[gwId] = room;
    });
    setGatewayRoomMap(map);
  }, [rooms]);

  // Get room for a gateway
  const getRoomForGateway = useCallback((gatewayId: string) => gatewayRoomMap[gatewayId], [gatewayRoomMap]);

  // Get gateway ID for a room
  const getGatewayForRoom = useCallback((room: Room) => generateGatewayId(room), []);

  // Send beacon and sensor signals to the API
  const sendSignals = useCallback(async () => {
    if (!organizationId) return;

    // Group beacons by their gateway location
    const beaconsByGateway: Record<string, string[]> = {};

    Object.entries(beaconLocations).forEach(([beaconId, gatewayId]) => {
      if (gatewayId) {
        if (!beaconsByGateway[gatewayId]) beaconsByGateway[gatewayId] = [];
        beaconsByGateway[gatewayId].push(beaconId);
      }
    });

    // Send gateway heartbeats for all test gateways (even without beacons)
    const allTestGateways = Object.keys(gatewayRoomMap);

    const requests = allTestGateways.map(gatewayId => {
      const beaconsAtGateway = beaconsByGateway[gatewayId] || [];

      const payload = [
        {
          gatewayFree: 91,
          timestamp: new Date().toISOString(),
          type: 'Gateway',
          gatewayLoad: 0.25,
          mac: gatewayId,
        },
        ...beaconsAtGateway.map(beaconId => ({
          ibeaconMinor: 0,
          ibeaconTxPower: IBEACON_TX_POWER,
          mac: beaconId,
          rssi: signalStrength + Math.round((Math.random() - 0.5) * SIGNAL_VARIANCE),
          ibeaconUuid: IBEACON_UUID,
          rawData: `0201060303E1FF1016E1FFA108${toHex(beaconBatteries[beaconId] || DEFAULT_BATTERY_LEVEL)}9B4D263F23AC504C5553`,
          type: 'Unknown',
          timestamp: new Date().toISOString(),
          ibeaconMajor: 0,
          bleName: `MiniBeacon_${beaconId}`,
        })),
      ];

      return fetch(`${config.apiURL}/minew?hospital=${organizationId}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-type': 'application/json' },
      });
    });

    // Send sensor signals (sensors are always sent from first test gateway for simplicity)
    const firstGatewayId = allTestGateways[0];
    if (firstGatewayId) {
      const enabledSensors = TEST_SENSORS.filter(s => sensorStates[s.id]?.enabled);

      if (enabledSensors.length > 0) {
        const sensorPayload = [
          {
            gatewayFree: 91,
            timestamp: new Date().toISOString(),
            type: 'Gateway',
            gatewayLoad: 0.25,
            mac: firstGatewayId,
          },
          ...enabledSensors.map(sensor => {
            const state = sensorStates[sensor.id];
            // Add small variance to simulate real sensor readings
            const tempWithVariance = state.temperature + (Math.random() - 0.5) * SENSOR_TEMP_VARIANCE * 2;
            const humidityWithVariance = state.humidity + (Math.random() - 0.5) * SENSOR_HUMIDITY_VARIANCE * 2;

            return {
              mac: sensor.id,
              rssi: signalStrength + Math.round((Math.random() - 0.5) * SIGNAL_VARIANCE),
              rawData: generateSensorRawData(
                tempWithVariance,
                humidityWithVariance,
                DEFAULT_SENSOR_BATTERY,
                sensor.model,
                sensor.hasHumidity
              ),
              type: 'Unknown',
              timestamp: new Date().toISOString(),
              bleName: sensor.label,
            };
          }),
        ];

        // Debug: Log full payload to verify format
        console.log('[Sensor Simulation] Sending sensor data:', {
          gateway: firstGatewayId,
          sensorCount: enabledSensors.length,
          sensors: enabledSensors.map(s => ({
            mac: s.id,
            temp: sensorStates[s.id]?.temperature,
            humidity: sensorStates[s.id]?.humidity,
          })),
          // Log first sensor's rawData to verify format matches backend pattern:
          // Expected: 020106[xx]FF3906CA0501[xxxx][temp:4hex][hum:4hex]...
          sampleRawData: sensorPayload[1]?.rawData,
        });

        requests.push(
          fetch(`${config.apiURL}/minew?hospital=${organizationId}`, {
            method: 'POST',
            body: JSON.stringify(sensorPayload),
            headers: { 'Content-type': 'application/json' },
          }).then(res => {
            console.log('[Sensor Simulation] Sensor API response:', res.status);
            return res;
          })
        );

        // Send V3Info frames sporadically (every 5th sync) for battery/firmware updates
        // This mimics real sensor behavior where info frames are sent less frequently
        // Use ref to avoid dependency loop (syncCount in deps causes infinite re-renders)
        // V3Info must be sent as JSON with type="V3Info", battery (float), version (string)
        // Skip first sync (count=0) to ensure sensor exists before V3Info arrives
        if (syncCountRef.current > 0 && syncCountRef.current % 5 === 0) {
          const v3InfoPayload = [
            {
              gatewayFree: 91,
              timestamp: new Date().toISOString(),
              type: 'Gateway',
              gatewayLoad: 0.25,
              mac: firstGatewayId,
            },
            ...enabledSensors.map(sensor => ({
              mac: sensor.id,
              rssi: signalStrength + Math.round((Math.random() - 0.5) * SIGNAL_VARIANCE),
              type: 'V3Info', // Must be V3Info for backend to parse battery/firmware
              timestamp: new Date().toISOString(),
              battery: DEFAULT_SENSOR_BATTERY, // Float value for battery percentage
              version: sensor.firmwareVersion, // Firmware version string
            })),
          ];

          console.log('[Sensor Simulation] Sending V3Info frames:', {
            syncCount: syncCountRef.current,
            sensorCount: enabledSensors.length,
            samplePayload: v3InfoPayload[1],
          });

          requests.push(
            fetch(`${config.apiURL}/minew?hospital=${organizationId}`, {
              method: 'POST',
              body: JSON.stringify(v3InfoPayload),
              headers: { 'Content-type': 'application/json' },
            }).then(res => {
              console.log('[Sensor Simulation] V3Info API response:', res.status);
              return res;
            })
          );
        }
      }
    }

    await Promise.all(requests);
    setLastSync(new Date());
    syncCountRef.current += 1;
    setSyncCount(syncCountRef.current);
  }, [organizationId, beaconLocations, beaconBatteries, signalStrength, gatewayRoomMap, sensorStates]);

  // Auto-sync interval
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(sendSignals, pingInterval);
    sendSignals(); // Initial sync

    return () => clearInterval(interval);
  }, [isRunning, pingInterval, sendSignals]);

  // Move beacon to room
  const moveBeaconToRoom = useCallback(
    (beaconId: string, room: Room | null) => {
      if (room) {
        const gatewayId = getGatewayForRoom(room);
        setBeaconLocations(prev => ({ ...prev, [beaconId]: gatewayId }));
      } else {
        setBeaconLocations(prev => {
          const next = { ...prev };
          delete next[beaconId];
          return next;
        });
      }
      setSelectedBeacon(null);
    },
    [getGatewayForRoom]
  );

  // Clear all beacon locations
  const clearAllLocations = useCallback(() => {
    setBeaconLocations({});
  }, []);

  // Get beacon's current location display
  const getBeaconLocationDisplay = useCallback(
    (beaconId: string): string | null => {
      const gatewayId = beaconLocations[beaconId];
      if (!gatewayId) return null;
      const room = getRoomForGateway(gatewayId);
      return room ? room.name : gatewayId;
    },
    [beaconLocations, getRoomForGateway]
  );

  // Get beacons in a room
  const getBeaconsInRoom = useCallback(
    (room: Room) => {
      const gatewayId = getGatewayForRoom(room);
      return TEST_BEACONS.filter(b => beaconLocations[b.id] === gatewayId);
    },
    [beaconLocations, getGatewayForRoom]
  );

  return {
    isRunning,
    signalStrength,
    pingInterval,
    selectedBeacon,
    beaconLocations,
    gatewayRoomMap,
    lastSync,
    syncCount,
    sensorStates,
    setIsRunning,
    setSignalStrength,
    setPingInterval,
    setSelectedBeacon,
    moveBeaconToRoom,
    clearAllLocations,
    sendSignals,
    updateSensorState,
    getBeaconLocationDisplay,
    getBeaconsInRoom,
    getRoomForGateway,
    getGatewayForRoom,
  };
};
